import type { EventAction, Level, Priority, Verdict, Visibility } from './constants';
import { format, verbatim, type Message } from '@/i18n';

// --- Result -----------------------------------------------------------------
// Discriminated-union result instead of throwing for expected domain outcomes
// (forbidden actor, invalid state, no-op). Infrastructure failures still throw.
export type Result<T> = { ok: true; value: T } | { ok: false; error: DomainError };

export interface DomainError {
  code: DomainErrorCode;
  /** The sentence in English: what a log line, an agent, and a surface not yet
   *  converted to the viewer's locale read. */
  message: string;
  /** The same sentence as a descriptor (docs/I18N.md §6), what a surface
   *  formats in the viewer's locale. Required: build an error with
   *  `domainError` / `errOf`, never as a literal. */
  descriptor: Message;
}

export type DomainErrorCode =
  | 'forbidden' // actor lacks authority for this command
  | 'invalid_state' // command not applicable in the current state
  | 'no_change' // command would not change anything
  | 'not_found' // the target entity does not exist
  | 'invalid_input' // malformed payload
  | 'no_credential' // the person holds no password to change or reset (identity plane, MODEL.md §6c)
  | 'rate_limited' // the actor is issuing this action too fast (abuse control; temporary)
  | 'unauthorized' // no credential, or one that no longer resolves (a revoked agent key)
  | 'internal'; // an infrastructure step failed (e.g. an email could not be sent) after the domain write

export function ok<T>(value: T): Result<T> {
  return { ok: true, value };
}

/** A typed error from a descriptor: `message` is its English, so every reader
 *  of `message` keeps reading the same sentence. */
export function domainError(code: DomainErrorCode, descriptor: Message): DomainError {
  return { code, message: format('en', descriptor), descriptor };
}

/** `err` from a descriptor: the converted form (docs/I18N.md). */
export function errOf(
  code: DomainErrorCode,
  descriptor: Message,
): { ok: false; error: DomainError } {
  return { ok: false, error: domainError(code, descriptor) };
}

/** What a surface formats for an error: its descriptor, or its English
 *  sentence as it is for a shape from outside the domain (a stored queue
 *  entry, a reply from an older build). */
export function errorMessage(error: { message: string; descriptor?: Message }): Message {
  return error.descriptor ?? verbatim(error.message);
}

// --- Shared domain validation util ------------------------------------------

/**
 * True when `v` is a plain object (not an array, not null). Used in every
 * JSON-payload parser to guard `value[key]` access before parsing fields.
 * Lives here so `definitions.ts` and `stage-gates.ts` share one copy rather
 * than defining the same 1-liner twice.
 */
export function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

// --- State the contract machine reasons about (docs/CONTRACT_SPINE.md §5) ----
export interface WorkItemState {
  // **verdict**: the requester's statement against the definition
  // (`open|disputed|accepted`), scoped to the delivery it judged.
  verdict: Verdict;
  // The requester seat(s): one person, several co-owners, or a group ref.
  // **Never empty** (§2 reserved seat); the creator is the default. Any requester
  // holds the verdict. These are the HELD requester seats (offers ride seat_offers).
  requesters: string[]; // ref-encoded, non-empty
  // The contributor seat(s): empty = open ("needs doing"); one or several
  // co-contributors, or a group ref. Any contributor has the same reach; the log
  // records WHO acted. HELD contributor seats (offers ride seat_offers).
  contributors: string[]; // ref-encoded; empty = open
  // The explicit lifecycle ATOMS (docs/CLOSED_AXIS.md, three-atom re-cut
  // 2026-07-22), each its own ORTHOGONAL axis, events remaining truth. There
  // is NO phase field: richer vocabulary belongs to a flow's authored mapping
  // exclusively (Leon's ruling 2026-07-21); an item in no flow has no phase.
  //   - `delivered`: the delivery standing (set by Tender, cleared by Retract) -
  //     the contributor's claim "I did the work". Never closes anything.
  //   - `active`: the DECLARED activity flag (set/cleared by SetActive; a
  //     declaration, kept honest by the coherence law - §1.3; `on_hold ⇒ ¬active`).
  //     PERSISTS through a close (orthogonality; closed dominates display).
  //   - `closed`: the CONCLUSION (set by Close, cleared by Reopen).
  //     `isClosed = closed`, nothing else. Done and cancelled are READINGS:
  //     done = closed ∧ delivered, cancelled = closed ∧ ¬delivered.
  delivered: boolean;
  active: boolean;
  closed: boolean;
  priority: Priority | null;
  acceptanceCriteria: string | null;
  description: string | null;
  // A short prose notice ("what is going on with this ticket"), shown in highlight
  // position on cards and the item page. Colourless context, never a health signal;
  // null = absent. Capped at NOTE_MAX_LENGTH in the decider.
  note: string | null;
  visibility: Visibility;
  // Whether this item inherits its ancestors' access grants down the
  // decomposition. Default true; false **breaks the chain** (MODEL.md §3).
  inheritsAccess: boolean;
  // Review required (docs/CLOSED_AXIS.md, 2026-11-03): the requester's setting
  // that the work goes through review before it is done - one source of the
  // review duty, a flow's review stage the other. Optional on the type so a
  // state built without it (a test, a list row) reads as not required.
  reviewRequired?: boolean;
  title: string;
  // The user tags the item carries, as FOLDS (docs/FIELDS_AND_TAGS.md §6), so
  // `Tag`/`Untag` can say `no_change`. Loaded only where one of those is
  // decided (the executor and the resolver's context builders, through
  // `itemTagFoldsOf`); absent = not loaded, read as nothing worn. Never part
  // of the `created` event: a birth's tags are their own `item_tagged` events.
  tagFolds?: readonly string[];
}

// --- A proposed event ------------------------------------------------------
// `newValue` is both the audit record and the projection delta. Its keys are
// WorkItem column names; the write spine applies it verbatim to the row. Keeping
// these identical means the log and the projection cannot disagree about a change.
/** The value of a user tag event (`item_tagged` / `item_untagged`): the tag
 *  itself, not a column delta (shape B, docs/FIELDS_AND_TAGS.md §9). */
export type ItemTagValue = {
  tag: string;
  fold: string;
};

export interface ProposedEvent {
  action: EventAction;
  previousValue: Partial<WorkItemState> | ItemTagValue | null;
  newValue: Partial<WorkItemState> | ItemTagValue | null;
  comment?: string;
  // The accountability level (Prinzip 20). Absent = routine `info`; `warning` for
  // an out-of-role action, `critical` for self-dealing on a verdict. Flags, never
  // blocks.
  level?: Level;
}

// --- Commands (intent) ------------------------------------------------------
/**
 * Input to create a contract (docs/CONTRACT_SPINE.md §7, D9). Placement is
 * optional and mutually exclusive at most one of {groupId, boardId}:
 *   - `boardId`: create ON a board (the birth recipe runs server-side: grant to
 *     the board's scope group, pin if the board is curated, set phase from lane).
 *   - `groupId`: grant directly to a group (no board, no pin).
 *   - neither: floating (creator seats only; item appears in "Ungrouped" on home).
 * `parentItemId` is independent of placement (a sub-task can be floating too).
 */
export interface CreateWorkItemInput {
  orgId: string;
  title: string;
  // The requester set defaults to [creator] when omitted (the reserved seat is
  // never empty, §2). contributors default to [] (open).
  requesters?: string[];
  contributors?: readonly string[];
  // Placement: at most one of boardId / groupId (the birth recipe, §7).
  boardId?: string | null;
  groupId?: string | null;
  parentItemId?: string | null;
  // The view the create is made THROUGH without being placed on it: a rooted
  // checklist's typed line is born under the root and never pinned
  // (docs/CHECKLIST.md §3). Only the parent check reads it: the view's root is
  // a parent a member of its audience may name (docs/VIEWS.md §2b). Ignored
  // when `boardId` is set, which names the view itself.
  viaViewId?: string | null;
  // Stage lane at birth (docs/FLOW.md; only with boardId, on a stage-laned
  // board): the same transaction joins the board's linked flow at this stage,
  // and the stage's authored mapping seeds the birth atoms.
  flowPhaseKey?: string | null;
  priority?: Priority | null;
  acceptanceCriteria?: string | null;
  description?: string | null;
  visibility?: Visibility;
  // Defaults to true; the server passes the org's `inherit_access_default` policy.
  inheritsAccess?: boolean;
  // Module-field seeds set at birth, validated through the module kit and carried
  // on the `created` event's newValue (no silent module_data insert).
  moduleData?: Record<string, unknown>;
  // User tags applied at birth (docs/FIELDS_AND_TAGS.md §6.3: the `#word` typed
  // into a title rides the birth recipe). Each is validated, deduped by fold,
  // and logged as its own `item_tagged` after the `created` event.
  tags?: readonly string[];
}

export type Command =
  // The lifecycle ACTS + the activity declaration (docs/CLOSED_AXIS.md, the
  // three-atom re-cut): epistemically distinct verbs over the explicit atoms.
  //   - `Tender`: deliver (delivered=true). Legal from any undelivered standing
  //     (flag-not-block). `no_change` if already delivered.
  //   - `Retract`: withdraw the delivery (delivered=false); voids a standing
  //     verdict in the same event. `invalid_state` if not delivered.
  //   - `SetActive`: declare in-progress (`active`) or clear it. `no_change` if the
  //     flag is unchanged. (The `on_hold ⇒ ¬active` coupling is enforced at the write
  //     spine - §1.4 - not here; the pure decider only moves the flag.)
  | { type: 'Tender' }
  | { type: 'Retract' }
  | { type: 'SetActive'; active: boolean }
  // The conclusion axis (docs/CLOSED_AXIS.md):
  //   - `Close`: conclude (closed=true). The ONE conclusion, reversible - touches
  //     neither delivered, verdict, nor active (the declaration persists so
  //     Reopen restores it; a closed item merely READS inactive). The optional
  //     `reason` rides the `concluded` event (empty/whitespace = absent).
  //     Closing while undelivered is the cancellation reading; when the actor
  //     is not a solo seat it carries `level: 'warning'` (flag, never block).
  //     `no_change` if already closed.
  //   - `Reopen`: un-conclude (closed=false). delivered/verdict/active stay as
  //     they were. `invalid_state` if not closed.
  | { type: 'Close'; reason?: string }
  | { type: 'Reopen' }
  // Seat yourself into the contributor set, nothing more (§6). Held immediately.
  | { type: 'Claim' }
  // The verdict (§5.2). Accept may carry a note for the record ("Accept mit
  // Auflagen"); Dispute a reason; Resolve withdraws ANY standing verdict
  // (accepted or disputed) back to open - a logged, requester-graded act.
  | { type: 'Accept'; note?: string }
  | { type: 'Dispute'; reason?: string }
  | { type: 'Resolve' }
  | { type: 'SetPriority'; priority: Priority | null }
  | { type: 'SetAcceptanceCriteria'; text: string | null }
  | { type: 'SetDescription'; text: string | null }
  // Set/clear the short prose notice (highlight position). Either party may edit
  // it, on any phase, without an out-of-role flag; capped at NOTE_MAX_LENGTH.
  | { type: 'SetNote'; text: string | null }
  // Rename the item. The title is the contract's shared name, so either party may
  // edit it (no out-of-role flag), but it can never be empty.
  | { type: 'SetTitle'; text: string }
  | { type: 'SetVisibility'; visibility: Visibility } // open <-> protected
  | { type: 'SetInheritsAccess'; inheritsAccess: boolean } // break / restore the chain
  | { type: 'SetReviewRequired'; required: boolean } // the requester's review setting
  // Reassign a party set directly (hand-off, add, remove). Contributors: `to` is
  // the new set (`[]` = open). Requester: `to` is the new non-empty owner list;
  // emptying it is rejected (§2). Pushing a seat to another party via offer/take-up
  // is a separate server path (seat_offers).
  | { type: 'Reassign'; role: 'contributor'; to: readonly string[] }
  | { type: 'Reassign'; role: 'requester'; to: readonly string[] }
  | { type: 'Comment'; text: string }
  | { type: 'Acknowledge' }
  // The user tag system (docs/FIELDS_AND_TAGS.md §6.2): apply or remove a tag,
  // an ITEM act gated like any other. `tag` is typed text (`#` optional);
  // the decider validates the path and compares by fold.
  | { type: 'Tag'; tag: string }
  | { type: 'Untag'; tag: string };

// --- Accept with conditions (DESIGN.md "Accept mit Auflagen") ----------------
// A follow-up obligation spawned by the verdict, in the same transaction: an
// ordinary new contract (tech debt, rework, a circle-back), requested by the
// acceptor, linked to the accepted item by a `relates` edge with the verdict as
// provenance. `dueDate` seeds the schedule module's `endTime`.
export interface AcceptFollowUp {
  title: string;
  description?: string | null;
  dueDate?: string | null; // a TimeValue token (2026-09-30, 2026-W40, 2026-Q4)
}
